import React, { useState } from 'react';
import { ncService } from '../services/api';

/**
 * #179 — Import em massa de NCs via CSV (migração da planilha legada).
 *
 * Modal: baixar template → upload → preview (com erros por linha) → confirmar.
 * As NCs entram SEM IA — preservam o histórico que o cliente já tem (causa
 * raiz, ações, status) como registro auditável. Status default 'rascunho'.
 *
 * @param {function} onClose - fecha o modal sem refrescar
 * @param {function} onDone  - chamado após import bem-sucedido (refrescar lista)
 */
function NCImportCSV({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState(null);

  const baixarTemplate = async () => {
    setErro(null);
    try {
      const blob = await ncService.baixarTemplateImportCsv();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_import_nc.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (e) {
      setErro('Não foi possível baixar o template.');
    }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setErro(null);
    setResultado(null);
    setPreview(null);
    setLoading(true);
    try {
      const r = await ncService.importarCsv(f, false);
      setPreview(r);
    } catch (err) {
      setErro(err?.response?.data?.detail || 'Não foi possível ler o arquivo. Confira o template.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmar = async () => {
    if (!file) return;
    setConfirming(true);
    setErro(null);
    try {
      const r = await ncService.importarCsv(file, true);
      setResultado(r);
    } catch (err) {
      setErro(err?.response?.data?.detail || 'Erro ao importar. Tente novamente.');
    } finally {
      setConfirming(false);
    }
  };

  const validas = preview?.resumo?.validas || 0;
  const comErro = preview?.resumo?.com_erro || 0;

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    padding: '32px 16px', zIndex: 1000, overflowY: 'auto',
  };
  const card = {
    background: '#fff', borderRadius: '14px', width: '100%', maxWidth: '760px',
    padding: '24px', boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--primary, #006948)' }}>
            <span className="material-symbols-outlined" style={{ fontSize: 20, verticalAlign: 'middle', marginRight: 6 }}>upload_file</span>
            Importar planilha de NCs
          </h3>
          <button onClick={onClose} title="Fechar" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </header>

        {erro && (
          <div style={{ background: '#ffebee', color: '#93000a', padding: '10px 12px', borderRadius: 8, marginBottom: 12, fontSize: '0.85rem' }}>
            {erro}
          </div>
        )}

        {resultado ? (
          <div>
            <p style={{ fontSize: '0.95rem' }}>
              ✅ <strong>{resultado.criadas}</strong> NC(s) importada(s)
              {resultado.puladas ? ` · ${resultado.puladas} pulada(s)` : ''}.
            </p>
            {resultado.erros?.length > 0 && (
              <ul style={{ fontSize: '0.8rem', color: '#92400e', maxHeight: 160, overflowY: 'auto' }}>
                {resultado.erros.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <button onClick={() => (onDone ? onDone() : onClose())} style={btnPrimary}>Concluir</button>
            </div>
          </div>
        ) : (
          <>
            <p style={{ fontSize: '0.9rem', color: '#57657a', marginTop: 0 }}>
              Migre seu histórico de NCs de uma planilha. As NCs entram como <strong>registro</strong> (sem reanálise por IA),
              preservando a causa raiz, ações e status que você já tem. Você pode reanalisar uma NC individualmente depois.
            </p>

            <ol style={{ fontSize: '0.9rem', color: '#0d1b2a', paddingLeft: 18 }}>
              <li style={{ marginBottom: 10 }}>
                <button onClick={baixarTemplate} style={btnSecondary}>
                  <span className="material-symbols-outlined" style={{ fontSize: 18, verticalAlign: 'middle', marginRight: 4 }}>download</span>
                  Baixar template CSV
                </button>
                <span style={{ marginLeft: 8, color: '#57657a' }}>preencha com seus dados (só <em>descrição</em> é obrigatória).</span>
              </li>
              <li>
                <label style={btnSecondary}>
                  <span className="material-symbols-outlined" style={{ fontSize: 18, verticalAlign: 'middle', marginRight: 4 }}>upload</span>
                  {file ? 'Trocar arquivo' : 'Selecionar arquivo .csv'}
                  <input type="file" accept=".csv,text/csv" onChange={handleFile} style={{ display: 'none' }} />
                </label>
                {file && <span style={{ marginLeft: 8, fontSize: '0.82rem', color: '#57657a' }}>{file.name}</span>}
              </li>
            </ol>

            {loading && <p style={{ fontSize: '0.85rem', color: '#57657a' }}>Lendo o arquivo…</p>}

            {preview && (
              <div style={{ marginTop: 8 }}>
                <p style={{ fontSize: '0.9rem' }}>
                  <strong>{validas}</strong> linha(s) válida(s)
                  {comErro ? <span style={{ color: '#92400e' }}> · {comErro} com erro (serão puladas)</span> : ''}.
                </p>
                <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                        <th style={th}>#</th><th style={th}>Número</th><th style={th}>Descrição</th>
                        <th style={th}>Data</th><th style={th}>Status</th><th style={th}>Erros</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.linhas.map((l) => (
                        <tr key={l.linha} style={{ background: l.erros?.length ? '#fff5f5' : '#fff', borderTop: '1px solid #eef2f7' }}>
                          <td style={td}>{l.linha}</td>
                          <td style={td}>{l.numero || '—'}</td>
                          <td style={td}>{l.descricao || <em style={{ color: '#93000a' }}>vazia</em>}</td>
                          <td style={td}>{l.data_ocorrencia || '—'}</td>
                          <td style={td}>{l.status}</td>
                          <td style={{ ...td, color: '#93000a' }}>{l.erros?.join('; ') || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: 16, textAlign: 'right' }}>
                  <button onClick={onClose} style={{ ...btnSecondary, marginRight: 8 }}>Cancelar</button>
                  <button onClick={handleConfirmar} disabled={validas === 0 || confirming} style={{ ...btnPrimary, opacity: (validas === 0 || confirming) ? 0.5 : 1 }}>
                    {confirming ? 'Importando…' : `Importar ${validas} NC(s)`}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const th = { padding: '8px 10px', fontWeight: 700, color: '#57657a', whiteSpace: 'nowrap' };
const td = { padding: '6px 10px', verticalAlign: 'top' };
const btnPrimary = {
  background: 'var(--primary, #006948)', color: '#fff', border: 'none',
  borderRadius: 8, padding: '9px 16px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem',
};
const btnSecondary = {
  background: '#eef2f7', color: '#0d1b2a', border: '1px solid #d7dee8',
  borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
  display: 'inline-flex', alignItems: 'center',
};

export default NCImportCSV;
